import type { BatchSummary } from '../api/types';
import {
  etaFromSystemMinutes,
  formatRelativeTime,
  formatTime,
} from '../lib/eta_calculator';
import { ageHuman, formatTimeMoscow } from '../lib/format';
import './BatchSummaryCard.css';

/** Maximum length for the tag display before we truncate with an ellipsis.
 *  Production tags are usually shorter than this (``Test-2 10_gps,glo,...``
 *  ≈ 30 chars) but the operator can set arbitrarily long ones via the
 *  /batch form — clip rather than wrap so the card stays one-line-tall. */
const TAG_MAX_LEN = 40;

function truncateTag(tag: string): string {
  return tag.length > TAG_MAX_LEN ? tag.slice(0, TAG_MAX_LEN - 1) + '…' : tag;
}

interface Props {
  batch: BatchSummary;
  onClick: () => void;
}

/** Phase 3.14 — compact batch summary card rendered in the
 *  "Активные партии" section at the top of the Feed.
 *
 *  Sections, top to bottom:
 *
 *  1. **Tag + start time** — operator's batch label (truncated to 40
 *     chars) plus "Запущен 12:34 · 8 ч назад". The wall-clock time
 *     is forced to Moscow via :func:`formatTimeMoscow` (Phase 3.10.1
 *     convention — backend ships UTC, frontend pins to Europe/Moscow
 *     so a user opening the Mini App from outside Russia sees the
 *     same time as the operator).
 *  2. **Progress bar** — ``progress_percent`` rendered as a thin fill
 *     with a "X% (terminal / total)" label below.
 *  3. **State counts** — emoji pills for ``done`` / ``failed`` /
 *     ``submitted`` / ``pending`` / ``quality_warnings``. Zero counts
 *     are hidden so a fresh batch doesn't read ``0 ✅ 0 ❌ 0 🔄`` etc.
 *  4. **Mean dR** — only when ``avg_done_dr_3d_mm`` is non-null
 *     (we have at least one DONE residual that survived the Phase
 *     3.13.2 anomaly filter).
 *
 *  Click anywhere on the card triggers ``onClick`` — FeedScreen wires
 *  this to set ``?tag=…`` so the existing tag-filter badge (Phase 3.6)
 *  takes over and the jobs list below auto-filters.
 */
export function BatchSummaryCard({ batch, onClick }: Props) {
  const startedAt = formatTimeMoscow(batch.first_created_at);
  const startedAgo = ageHuman(batch.first_created_at);
  const terminal =
    batch.counts.done + batch.counts.failed + batch.counts.cancelled;

  return (
    <button
      type="button"
      className="batch-summary-card"
      onClick={onClick}
      aria-label={`Фильтр по серии ${batch.tag}`}
    >
      <div className="batch-tag" title={batch.tag}>
        {truncateTag(batch.tag)}
      </div>
      <div className="batch-meta">
        Запущен {startedAt} · {startedAgo}
      </div>

      <div className="batch-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${batch.progress_percent}%` }}
          />
        </div>
        <div className="progress-text">
          {Math.round(batch.progress_percent)}% ({terminal} из{' '}
          {batch.total_jobs})
        </div>
      </div>

      <div className="batch-counts">
        {batch.counts.done > 0 && (
          <span className="count-done">✅ {batch.counts.done}</span>
        )}
        {batch.counts.failed > 0 && (
          <span className="count-failed">❌ {batch.counts.failed}</span>
        )}
        {batch.counts.submitted > 0 && (
          <span className="count-active">🔄 {batch.counts.submitted}</span>
        )}
        {batch.counts.pending > 0 && (
          <span className="count-pending">⏳ {batch.counts.pending}</span>
        )}
        {batch.counts.cancelled > 0 && (
          <span className="count-cancelled">⊘ {batch.counts.cancelled}</span>
        )}
        {batch.quality_warnings > 0 && (
          <span className="count-warning">⚠ {batch.quality_warnings}</span>
        )}
        {/* Phase 4.7 — auto-substitute counter. Uses 🔁 (clockwise
            repeat) rather than 🔄 (counterclockwise refresh) so the
            pill doesn't visually clash with the in-flight ``submitted``
            pill, which already uses 🔄. The Telegram message keeps 🔄
            in the body text — the contexts differ enough that the
            divergence is fine. */}
        {(batch.substitution_count ?? 0) > 0 && (
          <span
            className="count-substitution"
            title={`Автозамена: ${batch.substitution_count} задач(и)`}
          >
            🔁 {batch.substitution_count}
          </span>
        )}
      </div>
      {/* Phase 4.9 — hid the «Среднее |dR| (DONE)» line. The current
          residual computation has 15-1640 mm spread depending on the
          reduction-model mismatch, so the displayed mean misleads more
          than it informs. The field stays in the API response
          (``BatchSummary.avg_done_dr_3d_mm``) for forward compat;
          Phase 3.16 v2 will re-enable the render once the metric is
          trustworthy. */}
      {/* Phase 4.13 — auto-substitute summary row. Distinct from
          the «🔁 N» chip in the counts row above: the chip is a
          glanceable per-batch tally; this row is the descriptive
          form the operator scans when they're trying to understand
          WHY the batch produced fewer DONE rows than they expected
          (one of the failed substations got replaced). Rendered
          only when the count is non-zero so legacy batches with
          no substitutions look the same as before. */}
      {(batch.substitution_count ?? 0) > 0 && (
        <div className="batch-substitutions">
          🔄 Автозамен: {batch.substitution_count}
        </div>
      )}
      {/* Phase 4.13 — «реально получено» summary line for completed
          batches. Distinct from ``counts.done`` because two
          successful configs of the same station contribute 2 to
          ``counts.done`` but only 1 to ``unique_stations_done`` —
          the operator's mental model is «how many stations did I
          actually get?», not «how many config-jobs did I get».
          The «of N запрошенных» suffix subtracts substitutes so a
          random_n=10 batch that fell back to 1 replacement still
          reads «из 10» (not «из 11»). */}
      {batch.is_completed &&
        (batch.requested_station_count ?? 0) > 0 && (
          <div className="batch-stations-summary">
            ℹ Уникальных DONE-станций: {batch.unique_stations_done ?? 0}{' '}
            из {batch.requested_station_count} запрошенных
          </div>
        )}
      {/* Phase 4.13 — ETA row. Hidden when the batch is complete
          (no remaining work) OR when the backend reports 0
          system-minutes left (a freshly-created batch where every
          job has been moved out of pending/submitted, or a batch
          with no recognised GNSS arrays — defensive). The render
          path is synchronous: ``finishesAt`` is computed from
          ``Date.now() + wallClock × 60_000`` at render time so each
          re-poll of the batches list (the FeedScreen polls every
          30 s) refreshes the «HH:MM» suffix automatically. */}
      {(() => {
        // Phase 4.5.2 — prefer the backend-computed ETA when
        // present. It reads live ``SCH_MAX_CONCURRENT_JOBS`` and
        // the empirical per-task median, so it tracks parallelism
        // bumps and server swaps without a frontend rebuild. Fall
        // back to the v1 frontend formula on the rare path where
        // the API somehow ships the new field as undefined —
        // pre-4.5.2 deploys or a future backend regression won't
        // blank out the ETA row.
        const backendMinutes = batch.eta_minutes_remaining;
        if (typeof backendMinutes === 'number' && backendMinutes > 0) {
          const finishesAt = new Date(Date.now() + backendMinutes * 60_000);
          return (
            <div className="batch-eta">
              <span className="eta-icon" aria-hidden>
                ⏱
              </span>
              <span className="eta-label">примерно завершится</span>
              <span className="eta-value">
                {formatRelativeTime(backendMinutes)} •{' '}
                {formatTime(finishesAt)}
              </span>
            </div>
          );
        }
        // Legacy fallback — same formula as before Phase 4.5.2.
        const remainingCount =
          (batch.counts.pending ?? 0) + (batch.counts.submitted ?? 0);
        const eta = etaFromSystemMinutes(
          batch.remaining_system_minutes ?? 0,
          remainingCount,
        );
        if (!eta) return null;
        return (
          <div className="batch-eta">
            <span className="eta-icon" aria-hidden>
              ⏱
            </span>
            <span className="eta-label">примерно завершится</span>
            <span className="eta-value">
              {formatRelativeTime(eta.minutes)} •{' '}
              {formatTime(eta.finishesAt)}
            </span>
          </div>
        );
      })()}
    </button>
  );
}
